<template>
	<div class="mx-7">
    <v-btn small fab class="mb-3" color="primary" @click="clickNewEstablishment">
			<v-icon>mdi-plus</v-icon>
		</v-btn>
		<p v-if="establishments.length == 0" class="font-weight-thin ml-2" v-translate>No establishments.</p>
    <v-card v-else v-for="establishment in establishments" :key="establishment.id" class="mb-2">
      <v-card-title>
        <v-icon :color="establishment.color" class="mr-3">mdi-domain</v-icon>
        {{establishment.name}}
        <v-spacer></v-spacer>
        <v-btn icon @click="clickEditEstablishment(establishment)">
          <v-icon color="primary">edit</v-icon>
        </v-btn>
        <v-btn icon @click="showDeleteEstablishment = true">
          <v-icon color="primary">delete</v-icon>
          <DeleteConfirmationDialog v-model="showDeleteEstablishment" item="establishment" @delete="deleteEstablishment(establishment)" />
        </v-btn>
      </v-card-title>
      <v-card-text class="pa-6 pt-3">
        <v-row no-gutters>
          <v-col cols="5">
            <p v-if="establishment.address">
              <span v-translate class="font-weight-medium">Address</span><span class="ml-2">{{establishment.address}}</span>
            </p>
            <p v-if="establishment.phone">
              <span v-translate class="font-weight-medium">Phone</span><span class="ml-2">{{establishment.phone}}</span>
            </p>
            <p v-if="establishment.email">
              <span v-translate class="font-weight-medium">Email</span><span class="ml-2">{{establishment.email}}</span>
            </p>
          </v-col>
          <v-col cols="7">
            <v-row no-gutters align="center">
              <v-col cols="auto" class="mr-4">
                <p v-if="establishment.prescription_header">
                  <span v-translate class="font-weight-medium">Prescription Header</span><br>
                  <span style="white-space: pre-wrap">{{establishment.prescription_header}}</span>
                </p>
                <p v-if="establishment.prescription_footer">
                  <span v-translate class="font-weight-medium">Prescription Footer</span><br>
                  <span style="white-space: pre-wrap">{{establishment.prescription_footer}}</span>
                </p>
              </v-col>
              <v-col cols="auto" v-if="establishment.prescription_logo_url">
                <v-img
									:src="establishment.prescription_logo_url"
									contain
                  max-width="160px"
								/>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <p v-if="establishment.offices.length == 0" class="font-weight-thin ml-2 mt-2" v-translate>No offices.</p>
        <template v-else>
          <p v-translate class="mb-1 font-weight-medium">Offices</p>
          <div class="mt-2">
            <p v-for="office in establishment.offices" :key="office.id" class="mb-1 ml-2">{{office.name}}</p>
          </div>
        </template>
      </v-card-text>
    </v-card>
    <v-dialog v-model="showEstablishmentForm" max-width="50%" scrollable>
			<v-card>
				<v-card-title class="primary--text mb-3 secondary">
					<v-icon color="primary" class="mr-2">mdi-domain</v-icon>
					<translate key="edit" v-if="edit">Edit Establishment</translate>
          <translate key="new" v-else>New Establishment</translate>
				</v-card-title>
				<v-card-text>
          <v-form ref="establishmentForm">
            <v-row no-gutters class="mt-2">
              <v-col cols="12">
                <v-text-field
                  v-model="establishment.name"
                  :label="$gettext('Name')"
                  filled
                  class="px-2"
                  :rules="establishmentNameRules"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-row no-gutters justify="center">
                  <v-color-picker hide-inputs v-model="establishment.color"></v-color-picker>
                </v-row>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="establishment.address"
                  :label="$gettext('Address')"
                  filled
                  class="px-2"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="establishment.phone"
                  :label="$gettext('Phone')"
                  filled
                  class="px-2"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="establishment.email"
                  :label="$gettext('Email')"
                  filled
                  class="px-2"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-row no-gutters class="mb-3">
                  <span class="subtitle-1 font-weight-regular primary--text">
                    <translate>Prescription</translate>
                  </span>
                </v-row>
                <v-divider class="mb-6"></v-divider>
              </v-col>
              <v-col cols="12" sm="12">
								<v-checkbox
									v-model="establishment.prescription_letterhead"
									:label="$gettext('Letterhead Paper')"
									color="primary"
									class="my-0 text-caption"
								></v-checkbox>
							</v-col>
							<v-col v-if="establishment.prescription_letterhead" cols="12" md="4">
								<v-text-field
									v-model="establishment.prescription_letterhead_height"
									:label="$gettext('Letterhead height (mm)')"
									filled
									class="px-2"
                  type="number"
								></v-text-field>
							</v-col>
							<template v-else>
									<v-col cols="12" md="6" class="pl-2">
										<v-textarea
											filled
											:label="$gettext('Prescription Header')"
											v-model="establishment.prescription_header"
										></v-textarea>
									</v-col>
									<v-col cols="12" md="6" class="pl-2">
										<v-textarea
											filled
											:label="$gettext('Prescription Footer')"
											v-model="establishment.prescription_footer"
										></v-textarea>
									</v-col>
									<v-col cols="12" md="6" class="pl-2 mb-4">
										<v-row no-gutters justify="center" class="pa-4">
											<v-col cols="12">
											<v-img
												:src="establishment.prescription_logo_url ? establishment.prescription_logo_url : require('../../assets/images/generic_logo.png')"
												contain
												height="125"
											/>
											</v-col>
											<v-col cols="12" v-if="establishment.prescription_logo_url">
												<v-btn large icon @click="removePrescriptionLogo" :loading="removingPrescriptionLogo" style="float: right">
													<v-icon color="primary">delete</v-icon>
												</v-btn>
											</v-col>
										</v-row>
										<v-row no-gutters align="center" class="mb-5">
											<v-col>
												<VueFileAgent
                          :theme="'list'"
                          :multiple="false"
                          :deletable="true"
                          :meta="true"
                          :accept="'image/*'"
                          :maxSize="'50MB'"
                          :helpText="$gettext('Drag or click')"
                          :errorText="{
                            type: 'Invalid file type. Only images are allowed',
                            size: 'Files cannot exceed 50MB in size'
                          }"
													@select="!$event[0].error ? prescriptionLogo = $event[0] : null"
                          @beforedelete="prescriptionLogo = null"
                          v-model="prescriptionLogo"
                        ></VueFileAgent>
											</v-col>
										</v-row>
									</v-col>
							</template>
              <v-col cols="12">
                <v-row no-gutters class="mb-3">
                  <span class="subtitle-1 font-weight-regular primary--text">
                    <translate>Offices</translate>
                  </span>
                </v-row>
                <v-row no-gutters>
                  <v-btn fab color="primary" small @click="clickNewOffice">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </v-row>
                <v-dialog v-model="showNewOfficeForm" max-width="40%" scrollable>
                  <v-card>
                    <v-card-title class="title primary--text mb-2 secondary">
                      <translate>Office</translate>
                    </v-card-title>
                    <v-card-text>
                      <v-form ref="officeForm">
                        <v-row no-gutters class="mt-2">
                          <v-col cols="12">
                            <v-text-field
                              v-model="office.name"
                              :label="$gettext('Name')"
                              filled
                              class="pa-2"
                              :rules="officeNameRules"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                      </v-form>
                    </v-card-text>
                    <v-divider></v-divider>
                    <v-card-actions>
                      <v-btn color="primary" text @click="showNewOfficeForm = false">
                        <translate>Cancel</translate>
                      </v-btn>
                      <v-spacer></v-spacer>
                      <v-btn color="primary" text @click="createOffice" key="save" :loading="savingOffice">
                        <translate>Save</translate>
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
                <p v-if="establishment.offices.length == 0" class="font-weight-thin ml-2 mt-2" v-translate>No offices.</p>
                <v-list v-else flat>
                  <AppointmentLocation v-for="office in establishment.offices" :key="office.id" :appLocation="office" @remove-appointment-location="removeOffice" />
                </v-list>
              </v-col>
            </v-row>
          </v-form>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-btn color="primary" text @click="showEstablishmentForm = false">
						<translate>Cancel</translate>
					</v-btn>
					<v-spacer></v-spacer>
          <v-btn key="save" v-if="edit" color="primary" text @click="saveEstablishment" :loading="loading">
						<translate>Save</translate>
					</v-btn>
					<v-btn key="create" v-else color="primary" text @click="createEstablishment" :loading="loading">
						<translate>Create</translate>
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import axios from "axios";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog.vue";
import AppointmentLocation from '@/components/settings/AppointmentLocation.vue';

export default {
  name: "Establishments",
  components: {
    DeleteConfirmationDialog,
    AppointmentLocation
  },
  data: () => ({
    showEstablishmentForm: false,
    showDeleteEstablishment: false,
    loading: false,
    edit: false,
    showNewOfficeForm: false,
    savingOffice: false,
    establishment: {
      name: "",
      color: "#FF0000",
      address: "",
      phone: "",
      email: "",
      offices: []
    },
    office: {
      name: ""
    },
    prescriptionLogo: null,
    removingPrescriptionLogo: false
  }),
  computed: {
    establishments() {
      return this.$store.getters.establishments;
    },
    required() {
      return value => !!value || this.$gettext("Required.");
    },
    establishmentNameRules() {
			return [v => !!v || this.$gettext("Name is required"), v => this.establishments.filter(e => e.name == v && this.establishment.id != e.id).length == 0 || this.$gettext("Name already exists.")];
		},
    officeNameRules() {
			return [v => !!v || this.$gettext("Name is required"), v => this.establishment.offices.filter(o => o.name == v).length == 0 || this.$gettext("Name already exists.")];
		}
  },
  methods: {
    clickEditEstablishment(establishment) {
      this.establishment = establishment;
      this.edit = true;
      this.showEstablishmentForm = true;
      this.prescriptionLogo = null;
    },
    clickNewEstablishment() {
      this.edit = false;
      this.showEstablishmentForm = true;
      setTimeout(() => this.resetEstablishment());
    },
    getEstablishmentForm() {
      if(this.$refs.establishmentForm.validate()) {
        let form = new FormData();
        form.append("name", this.establishment.name);
        form.append("color", this.establishment.color ?? '');
        form.append("address", this.establishment.address ?? '');
        form.append("phone", this.establishment.phone ?? '');
        form.append("email", this.establishment.email ?? '');
        form.append("prescription_header", this.establishment.prescription_header ?? '');
        form.append("prescription_footer", this.establishment.prescription_footer ?? '');
        form.append("prescription_letterhead", this.establishment.prescription_letterhead ?? false);
        if (this.establishment.prescription_letterhead) {
          form.append("prescription_letterhead_height", this.establishment.prescription_letterhead_height ?? 0);
        }
        if (this.prescriptionLogo) {
          form.append("prescription_logo_upload", this.prescriptionLogo.file);
        }
        form.append("offices_json", JSON.stringify(this.establishment.offices));
        return form;
      } else return false;
    },
    createEstablishment() {
      let form = this.getEstablishmentForm();
      if(form) {
        this.loading = true;
        axios
          .post("/establishments/new", form)
          .then(response => {
            console.log(response.data);
            if (response.data.result == "ok") {
              this.$store.dispatch("ADD_ESTABLISHMENT", response.data.establishment);
              this.showEstablishmentForm = false;
            }
          })
          .catch(error => {
            console.log(error);
          })
          .finally(() => this.loading = false);
      }
    },
    saveEstablishment() {
      let form = this.getEstablishmentForm();
      if(form) {
        this.loading = true;
        axios
          .post("/establishments/" + this.establishment.id + "/edit", form)
          .then(response => {
            console.log(response.data);
            if (response.data.result == "ok") {
              this.$store.dispatch("EDIT_ESTABLISHMENT", response.data.establishment);
              this.showEstablishmentForm = false;
            }
          })
          .catch(error => {
            console.log(error);
          })
          .finally(() => this.loading = false);
      }
    },
    deleteEstablishment(establishment) {
      axios
        .post("/establishments/" + establishment.id + "/delete")
        .then(response => {
          console.log(response.data);
          if (response.data.result == "ok") {
            this.$store.dispatch("DELETE_ESTABLISHMENT", establishment.id);
          }
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => this.showDeleteEstablishment = false);
    },
    removePrescriptionLogo() {
      this.removingPrescriptionLogo = true;
      axios
        .post(`/establishments/${this.establishment.id}/remove-prescription-logo`)
        .then(response => {
          console.log(response.data);
          if (response.data.result == "ok") {
            this.$store.dispatch("REMOVE_ESTABLISHMENT_LOGO", this.establishment);
          }
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => this.removingPrescriptionLogo = false);
    },
    clickNewOffice() {
      this.showNewOfficeForm = true;
      setTimeout(() => this.resetOffice());
    },
    getOfficeForm() {
      return this.$refs.officeForm.validate();
    },
    createOffice() {
      let valid = this.getOfficeForm();
      if(valid) {
        if(this.establishment.id) {
          this.savingOffice = true;
          this.office.establishment_id = this.establishment.id;
          axios
            .post("/appointment-location/new", this.office)
            .then(response => {
              console.log(response.data);
              this.addOffice(response.data.appointment_location);
              this.showNewOfficeForm = false;
            })
            .catch(error => {
              console.log(error);
            })
            .finally(() => this.savingOffice = false);
        } else {
          this.addOffice(JSON.parse(JSON.stringify(this.office)));
          this.showNewOfficeForm = false;
        }
      }
    },
    addOffice(office) {
      console.log(office);
      if(this.edit) this.$store.dispatch("ADD_ESTABLISHMENT_OFFICE", {establishment: this.establishment, office: office});
      else this.establishment.offices.push(office);
    },
    removeOffice(office) {
      if(this.edit) this.$store.dispatch("REMOVE_ESTABLISHMENT_OFFICE", {establishment: this.establishment, office: office});
      else this.establishment.offices.splice(this.establishment.offices.indexOf(office), 1);
    },
    resetEstablishment() {
      this.establishment = {
        name: "",
        color: "#FF0000",
        address: "",
        phone: "",
        email: "",
        offices: []
      };
      this.prescriptionLogo = null;
      this.$refs.establishmentForm.resetValidation();
    },
    resetOffice() {
      this.office = {
        name: ""
      };
      this.$refs.officeForm.resetValidation();
    }
  }
}
</script>